import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import {
  formatDate,
  formatNumber,
  getAbbrValue,
  getRandomColor,
} from '../../../core/helpers';
import './SellerChart.scss';
import { normalizeDateFE } from '../../../core/normalization';
import {
  Payment,
  ReportPeriod,
  SellerFilingReportToConfirm,
} from '../../../core/types';
import EmptyChartImage from '../../../images/empty-chart.svg';
import { isSameMonth, isSameYear } from 'date-fns';
import { useFlags } from 'launchdarkly-react-client-sdk';

interface IAmount {
  amount: number;
  currency: string;
}

interface NormalizedAmount {
  amount: number;
  eur: number;
}

interface Props {
  payments: Payment[];
  filingData: SellerFilingReportToConfirm[];
}

interface TooltipData {
  country: string;
  total: number;
  periods: ReportPeriod[];
}

export default function SellerChart({ payments, filingData }: Props) {
  const { vs3852PeriodsForPaymentsInChart } = useFlags();

  const [tooltipData, setTooltipData] = useState<TooltipData>();

  const { t, i18n } = useTranslation();

  const getCountryLabel = (c: string) => {
    return c === 'OSS' ? 'OSS' : t(c);
  };
  const lang = i18n.language;

  const keys = useMemo(
    () =>
      payments
        .map((d) => (d.type === 'local' ? d.Country.short_name : 'OSS'))
        .filter((value, index, self) => self.indexOf(value) === index)
        .map((c) => ({ id: c }))
        .sort((a, b) => {
          if (a.id === 'OSS') {
            return -1;
          }
          return t(a.id).localeCompare(t(b.id), lang);
        }),
    [t, lang, payments]
  );

  const normalizedChartData = useMemo(() => {
    const dates = payments
      .map((c) => normalizeDateFE(c.due_date))
      .sort((a, b) => a.getTime() - b.getTime())
      .filter(
        (d, i, self) =>
          self.findIndex(
            (date) => isSameMonth(d, date) && isSameYear(d, date)
          ) === i
      );

    return dates.map((date) => {
      const curPayments = payments.filter((d) => {
        const dueDate = normalizeDateFE(d.due_date);
        return isSameMonth(date, dueDate) && isSameYear(date, dueDate);
      });

      return {
        date,
        periods: vs3852PeriodsForPaymentsInChart
          ? filingData
              .filter((f) =>
                curPayments.some(
                  (p) => p.payment_instruction_id === f.payment_instruction.id
                )
              )
              .map((f) => ({
                country: f.type === 'oss' ? 'OSS' : f.country.short_name,
                period: f.period,
              }))
              .reduce(
                (
                  acc: {
                    [key: string]: ReportPeriod[];
                  },
                  item
                ) => {
                  acc[item.country] = [
                    ...(acc[item.country] || []),
                    item.period,
                  ];

                  return acc;
                },
                {}
              )
          : {},
        ...curPayments.reduce(
          (
            acc: {
              [key: string]: number;
            },
            item
          ) => {
            const eurAmount = item.Amounts.find(
              (a: IAmount) => a.currency === 'EUR'
            );

            acc[item.type === 'local' ? item.Country.short_name : 'OSS'] =
              (eurAmount?.amount || 0) / 100;

            return acc;
          },
          {}
        ),
      };
    });
  }, [payments, vs3852PeriodsForPaymentsInChart, filingData]);

  const paidTotal = useMemo(() => {
    const currencies = payments
      .map((a) => ({
        original_currency: a.original_currency,
        Amounts: a.Amounts.reduce(
          (
            acc: {
              [key: string]: number;
            },
            i: IAmount
          ) => {
            acc[i.currency] = (i.amount || 0) / 100;
            return acc;
          },
          {}
        ),
      }))
      .reduce(
        (
          acc: {
            [key: string]: NormalizedAmount;
          },
          a
        ) => {
          acc[a.original_currency] = {
            amount:
              (acc[a.original_currency]?.amount || 0) +
              a.Amounts[a.original_currency],
            eur: (acc[a.original_currency]?.eur || 0) + a.Amounts['EUR'],
          };
          return acc;
        },
        {}
      );

    return {
      ...currencies,
      EUR: {
        amount: payments
          .filter((item) => item.original_currency === 'EUR')
          .map((a) => a.Amounts[0])
          .reduce((acc, a) => {
            return (acc += (a.amount || 0) / 100);
          }, 0),
      },
    };
  }, [payments]);

  const pallete = useMemo(
    () =>
      keys.reduce(
        (
          acc: {
            [key: string]: string;
          },
          k
        ) => {
          acc[k.id] = palleteMap[k.id] || getRandomColor();
          return acc;
        },
        {}
      ),
    [keys]
  );

  if (!payments.length) {
    return (
      <div className='Paper px-4 py-3 mb-3'>
        <div className='fw-600 text-center mb-1'>
          {t("You don't have any VAT payments in 12 months.")}
        </div>
        <div className='text-center mb-3'>
          {t('Once you do any, you will see the distribution by country.')}
        </div>
        <div className='d-flex justify-content-center'>
          <img src={EmptyChartImage} alt='EmptyChartImage' />
        </div>
      </div>
    );
  }

  return (
    <div className='Paper px-4 py-3 mb-3'>
      <div className='mb-3 d-flex'>
        <div className='fw-600 me-3'>{t('VAT paid in 12 months')}</div>
        <div className='d-flex flex-wrap'>
          {Object.entries(paidTotal)
            .sort((a, b) => {
              if (a[0] === 'EUR') {
                return -1;
              }
              return a[0].localeCompare(b[0], i18n.language);
            })
            .map(([k, v]) => (
              <div key={k} className='me-2 SetupCheck__FCTransferCountryCol'>
                {'eur' in v
                  ? `${formatNumber(v.amount, {
                      currency: k,
                    })} (${formatNumber((v as NormalizedAmount).eur, {
                      currency: 'EUR',
                    })})`
                  : formatNumber(v.amount, {
                      currency: k,
                    })}
              </div>
            ))}
        </div>
      </div>
      <div
        className='w-100'
        style={{
          height: 300,
        }}
      >
        <ResponsiveContainer width='100%' height='100%'>
          <BarChart width={500} height={300} data={normalizedChartData}>
            <CartesianGrid vertical={false} strokeWidth={1} stroke='#DDE2E5' />
            <XAxis
              dataKey='date'
              tick={{
                fill: '#264F3F',
                fontSize: 11,
              }}
              stroke='#DDE2E5'
              strokeWidth={1}
              tickFormatter={(date: Date) => formatDate(date, 'MMM')}
            />
            <YAxis
              tickFormatter={(value: number) => getAbbrValue(value)}
              axisLine={false}
              tickLine={false}
              tick={({ y, payload }) => {
                return (
                  <g transform={`translate(${0},${y})`}>
                    <text
                      x={0}
                      y={0}
                      textAnchor='start'
                      fill='#929F9A'
                      fontSize={11}
                    >
                      {getAbbrValue(payload.value)}
                    </text>
                  </g>
                );
              }}
            />
            <Tooltip
              allowEscapeViewBox={{
                x: true,
                y: true,
              }}
              isAnimationActive={false}
              cursor={false}
              content={() => {
                if (tooltipData) {
                  return (
                    <div
                      className='SellerChart__tooltip position-relative multiline'
                      style={{ marginTop: -22, marginLeft: 22 }}
                    >
                      {getCountryLabel(tooltipData.country)}:{' '}
                      {tooltipData.total &&
                        formatNumber(tooltipData.total, {
                          currency: 'eur',
                        })}
                      {!!tooltipData.periods.length && (
                        <>
                          &nbsp;
                          {t('for')}{' '}
                          {tooltipData.periods
                            .flat()
                            .map((p) =>
                              formatDate(
                                normalizeDateFE(p.from_date),
                                getDateFormat(p.type)
                              )
                            )
                            .join(', ')}
                        </>
                      )}
                      <div className='SellerChart__tooltipArrow' />
                    </div>
                  );
                }
                return null;
              }}
            />
            <Legend
              iconType='circle'
              iconSize={8}
              verticalAlign='bottom'
              align='left'
              wrapperStyle={{
                left: 0,
                paddingBottom: 12,
              }}
              content={(props) => {
                const { payload } = props;
                return (
                  <div className='row row-cols-auto gx-3 w-100'>
                    {payload
                      ?.sort((a, b) => {
                        if (a.value === 'OSS') {
                          return -1;
                        }
                        return t(a.value).localeCompare(t(b.value), lang);
                      })
                      .map((entry) => {
                        return (
                          <div
                            className='col'
                            key={`item-${(entry as any).dataKey}`}
                          >
                            <div className='d-flex align-items-center user-select-none'>
                              <div
                                className='SellerChart__circle rounded-circle me-1'
                                style={{
                                  backgroundColor: entry.color,
                                }}
                              />
                              <span>{getCountryLabel(entry.value)}</span>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                );
              }}
            />
            {keys.map((k) => {
              const color = pallete[k.id];
              return (
                <Bar
                  className='cursor-pointer'
                  dataKey={k.id}
                  stackId='a'
                  key={k.id}
                  fill={color}
                >
                  {normalizedChartData.map((d, j) => (
                    <Cell
                      key={j}
                      fill={color}
                      onMouseEnter={() => {
                        setTooltipData({
                          country: k.id,
                          periods: (d.periods[k.id] || []).filter(
                            (p, i, self) =>
                              self.findIndex((fp) => fp.id === p.id) === i
                          ),
                          total: (
                            d as unknown as {
                              [key: string]: number;
                            }
                          )[k.id],
                        });
                      }}
                      onMouseLeave={() => {
                        setTooltipData(undefined);
                      }}
                    />
                  ))}
                </Bar>
              );
            })}
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

const getDateFormat = (type: 'month' | 'year' | 'quarter') => {
  switch (type) {
    case 'quarter':
      return 'QQQ yyyy';
    case 'year':
      return 'yyyy';
    default:
      return 'MMMM yyyy';
  }
};

export const palleteMap: {
  [key: string]: string;
} = {
  OSS: '#1C3FFD',
  AT: '#9B94B3',
  BE: '#D0523F',
  CZ: '#70E7D4',
  DK: '#1BBC9B',
  FR: '#B3CEDE',
  DE: '#362B40',
  IT: '#DD7842',
  LU: '#781C4F',
  NL: '#FFF176',
  PL: '#E9E08F',
  SI: '#BC8457',
  ES: '#3E7942',
  SE: '#722BF1',
  GB: '#F2BFE1',
};
